import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch all video collections (kind 30005) for a specific user.
 */
export function useCollections(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['collections', pubkey],
    queryFn: async ({ signal }) => {
      if (!pubkey) return [];

      const events = await nostr.query(
        [{ kinds: [30005], authors: [pubkey], limit: 100 }],
        { signal }
      );

      // Sort by created_at descending (newest first)
      return events.sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!pubkey,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Extract collection identifier from event
 */
export function getCollectionId(event: NostrEvent): string {
  const dTag = event.tags.find(([name]) => name === 'd');
  return dTag?.[1] ?? event.id;
}

/**
 * Extract collection title from event
 */
export function getCollectionTitle(event: NostrEvent): string {
  const titleTag = event.tags.find(([name]) => name === 'title');
  return titleTag?.[1] ?? 'Untitled Collection';
}

/**
 * Extract collection description from event
 */
export function getCollectionDescription(event: NostrEvent): string {
  const descriptionTag = event.tags.find(([name]) => name === 'description');
  return descriptionTag?.[1] ?? '';
}

/**
 * Extract video event IDs from a collection event
 */
export function getCollectionVideos(event: NostrEvent): string[] {
  return event.tags
    .filter(([name]) => name === 'e')
    .map(([, eventId]) => eventId);
}